import React, { useState, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Chip,
  Box,
  TextField,
  InputAdornment,
  FormHelperText,
  CircularProgress,
  Alert,
  SxProps,
  Theme,
} from '@mui/material';
import {
  Search as SearchIcon,
  Description as ContractIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';

import { Contract } from '../../types';
import { contractService } from '../../services/contractService';
import { getStatusColor, getStatusLabel } from '../../utils/contractStatusUtils';

interface ContractsActiveSelectProps {
  value?: number | number[] | null;
  onChange: (value: number | number[] | null) => void;
  label?: string;
  size?: 'small' | 'medium';
  error?: boolean;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  multiple?: boolean;
  placeholder?: string;
  allowClear?: boolean;
  fullWidth?: boolean;
  variant?: 'outlined' | 'filled' | 'standard';
  clientId: number; // ID do cliente para buscar contratos
  sx?: SxProps<Theme>;
}

const ContractsActiveSelect: React.FC<ContractsActiveSelectProps> = ({
  value,
  onChange,
  label = 'Contratos Ativos',
  size = 'medium',
  error = false,
  helperText,
  required = false,
  disabled = false,
  multiple = false,
  placeholder = 'Selecione um contrato',
  allowClear = false,
  fullWidth = true,
  variant = 'outlined',
  clientId,
  sx,
}) => {
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  // Carregar contratos ativos
  useEffect(() => {
    if (!clientId) return;

    const timeoutId = setTimeout(() => {
      loadContracts();
    }, 300); // Debounce de 300ms

    return () => clearTimeout(timeoutId);
  }, [clientId, searchTerm]);

  const loadContracts = async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const response = await contractService.getByClientId(clientId);
      
      if (response.success) {
        let contractsData = response.data || [];
        
        // Filtrar por termo de busca se fornecido
        if (searchTerm) {
          contractsData = contractsData.filter(contract =>
            contract.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
            contract.id.toString().includes(searchTerm)
          );
        }
        
        setContracts(contractsData);
      } else {
        throw new Error(response.message || 'Erro ao carregar contratos');
      }
    } catch (err: any) {
      setLoadError(err.message || 'Erro ao carregar contratos');
      setContracts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event: any) => {
    const selectedValue = event.target.value;
    
    if (multiple) {
      // Para seleção múltipla
      if (Array.isArray(selectedValue)) {
        onChange(selectedValue.length > 0 ? selectedValue : null);
      } else if (typeof selectedValue === 'number') {
        onChange([selectedValue]);
      } else {
        onChange(null);
      }
    } else {
      // Para seleção única
      onChange(selectedValue === '' ? null : selectedValue);
    }
  };

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    onChange(null);
  };

  const handleRemoveChip = (contractId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    if (multiple && Array.isArray(value)) {
      const newValue = value.filter(id => id !== contractId);
      onChange(newValue.length > 0 ? newValue : null);
    }
  };

  const getSelectedContracts = (): Contract[] => {
    if (!value) return [];
    
    if (multiple) {
      // Garantir que seja sempre um array para seleção múltipla
      let arrayValue: number[] = [];
      if (Array.isArray(value)) {
        arrayValue = value;
      } else if (typeof value === 'string') {
        try {
          const parsed = JSON.parse(value);
          arrayValue = Array.isArray(parsed) ? parsed : [];
        } catch {
          arrayValue = [];
        }
      } else if (typeof value === 'number') {
        arrayValue = [value];
      }
      
      return contracts.filter(contract => arrayValue.includes(contract.id));
    } else if (!multiple && typeof value === 'number') {
      const contract = contracts.find(c => c.id === value);
      return contract ? [contract] : [];
    }
    
    return [];
  };

  const renderValue = (selected: any) => {
    if (!selected || (Array.isArray(selected) && selected.length === 0)) {
      return <span style={{ color: '#999' }}>{placeholder}</span>;
    }

    const selectedContracts = getSelectedContracts();
    
    if (multiple) {
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {selectedContracts.map((contract) => (
            <Chip
              key={contract.id}
              label={`${contract.titulo} (ID: ${contract.id})`}
              size="small"
              onDelete={(event) => handleRemoveChip(contract.id, event)}
              deleteIcon={<ClearIcon />}
              sx={{ 
                height: 24, 
                fontSize: '0.75rem',
                '& .MuiChip-deleteIcon': {
                  fontSize: '14px'
                }
              }}
            />
          ))}
        </Box>
      );
    } else {
      const contract = selectedContracts[0];
      return contract ? `${contract.titulo} (ID: ${contract.id})` : '';
    }
  };

  const formatContractLabel = (contract: Contract) => {
    let label = `${contract.titulo} (ID: ${contract.id})`;
    
    if (contract.status !== 'ativo') {
      label += ` (${contract.status})`;
    }
    
    return label;
  };

  return (
    <FormControl 
      fullWidth={fullWidth} 
      size={size} 
      error={error} 
      variant={variant}
      disabled={disabled}
      sx={sx}
    >
      <InputLabel required={required}>
        {label}
      </InputLabel>
      
      <Select
        value={(() => {
          const computedValue = (() => {
            if (multiple) {
              // Garantir que seja sempre um array para seleção múltipla
              if (Array.isArray(value)) {
                return value;
              } else if (typeof value === 'string') {
                try {
                  const parsed = JSON.parse(value);
                  return Array.isArray(parsed) ? parsed : [];
                } catch {
                  return [];
                }
              } else if (typeof value === 'number') {
                return [value];
              }
              return [];
            } else {
              return value || '';
            }
          })();
          return computedValue;
        })()}
        label={label}
        onChange={handleChange}
        onOpen={() => setOpen(true)}
        onClose={() => {
          setOpen(false);
          setSearchTerm(''); // Limpar busca ao fechar
        }}
        multiple={multiple}
        renderValue={renderValue}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 400,
            },
          },
        }}
        startAdornment={
          <InputAdornment position="start">
            <ContractIcon />
          </InputAdornment>
        }
        endAdornment={
          allowClear && value && (
            <InputAdornment position="end">
              <ClearIcon 
                onClick={handleClear}
                sx={{ 
                  cursor: 'pointer', 
                  fontSize: '20px',
                  '&:hover': { color: 'error.main' }
                }}
              />
            </InputAdornment>
          )
        }
      >
        {/* Campo de busca */}
        {open && (
          <Box sx={{ p: 1, borderBottom: '1px solid #e0e0e0' }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar contrato..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fafafa'
                }
              }}
            />
          </Box>
        )}

        {/* Loading */}
        {loading && (
          <MenuItem disabled>
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={16} />
              <span>Carregando contratos...</span>
            </Box>
          </MenuItem>
        )}

        {/* Erro */}
        {loadError && (
          <MenuItem disabled>
            <Alert severity="error" sx={{ width: '100%', py: 0.5 }}>
              {loadError}
            </Alert>
          </MenuItem>
        )}

        {/* Opção vazia (apenas para seleção única) */}
        {!multiple && !required && !loading && !loadError && (
          <MenuItem value="">
            <em>Nenhum contrato</em>
          </MenuItem>
        )}

        {/* Lista de contratos */}
        {!loading && !loadError && contracts.map((contract) => {
          const contractId = Number(contract.id);
          return (
          <MenuItem key={contract.id} value={contractId} disabled={contract.status === 'cancelado'}>
            <Box display="flex" alignItems="center" width="100%">
              {multiple && (
                <Checkbox
                  checked={(() => {
                    if (!value) return false;
                    if (Array.isArray(value)) {
                      return value.includes(contractId);
                    } else if (typeof value === 'string') {
                      try {
                        const parsed = JSON.parse(value);
                        return Array.isArray(parsed) ? parsed.includes(contractId) : false;
                      } catch {
                        return false;
                      }
                    } else if (typeof value === 'number') {
                      return value === contractId;
                    }
                    return false;
                  })()}
                  size="small"
                />
              )}
              
              <Box display="flex" alignItems="center" gap={1} flex={1}>
                <ContractIcon fontSize="small" />
                
                <Box flex={1}>
                  <ListItemText
                    primary={
                      <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                        <span>{contract.titulo}</span>
                        <Chip
                          label={getStatusLabel(contract.status)}
                          size="small"
                          color={getStatusColor(contract.status) as any}
                          variant="outlined"
                          sx={{ height: 18, fontSize: '0.6rem' }}
                        />
                      </Box>
                    }
                    secondary={
                      <Box>
                        <div>ID: {contract.id}</div>
                        <div style={{ fontSize: '0.75rem', color: '#666' }}>
                          Vencimento: Dia {contract.dia_vencimento} | 
                          Valor: R$ {Number(contract.valor_contratado || 0).toFixed(2).replace('.', ',')}
                        </div>
                      </Box>
                    }
                    primaryTypographyProps={{ 
                      fontSize: '0.875rem',
                      fontWeight: contract.status === 'ativo' ? 'bold' : 'normal'
                    }}
                    secondaryTypographyProps={{ 
                      fontSize: '0.75rem',
                      color: contract.status === 'ativo' ? 'text.secondary' : 'error.main'
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </MenuItem>
          );
        })}

        {/* Nenhum resultado */}
        {!loading && !loadError && contracts.length === 0 && (
          <MenuItem disabled>
            <em>Nenhum contrato ativo encontrado</em>
          </MenuItem>
        )}
      </Select>

      {helperText && (
        <FormHelperText>{helperText}</FormHelperText>
      )}
    </FormControl>
  );
};

export default ContractsActiveSelect;
